import { useSignal } from '@preact/signals';
import type { ComponentChildren } from 'preact';
import { useEffect } from 'preact/hooks';

import type { EditScope } from '@amumin/api-client';
import { t } from '@amumin/i18n';
import { Button, Dialog, RadioList } from '@amumin/ui';

import { appText, messages } from '../state/session.js';

/** Bevestigen van één (meestal destructieve) actie: vraag als titel, één zin uitleg. */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  danger = false,
  busy = false,
  onConfirm,
  onClose,
  children,
  name = 'confirm-dialog',
}: {
  open: boolean;
  title: string;
  body?: string;
  confirmLabel: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
  children?: ComponentChildren;
  name?: string;
}) {
  const m = messages.value;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      {...(body ? { description: body } : {})}
      closeLabel={m.design.dialog.close}
      dismissible={!danger}
      name={name}
      actions={
        <>
          <Button name={`${name}-cancel`} onPress={onClose}>
            {appText.value.common.cancel}
          </Button>
          <Button
            name={`${name}-confirm`}
            variant={danger ? 'danger' : 'primary'}
            busy={busy}
            onPress={onConfirm}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children}
    </Dialog>
  );
}

/**
 * Bereikvraag bij herhalingen (ONTWERP §5.3, DESIGN §8.W3): "Alleen deze keer / Deze en
 * volgende / De hele reeks". `allowed` beperkt de keuzes (bv. herhaling gewijzigd → geen "deze").
 */
export function ScopeDialog({
  open,
  mode,
  dateLabel,
  allowed = ['this', 'this_and_following', 'all'],
  onChoose,
  onClose,
  busy = false,
}: {
  open: boolean;
  mode: 'edit' | 'delete';
  /** "woensdag 23 september" */
  dateLabel: string;
  allowed?: ReadonlyArray<EditScope>;
  onChoose: (scope: EditScope) => void;
  onClose: () => void;
  busy?: boolean;
}) {
  const m = messages.value;
  const a = appText.value;
  const value = useSignal<EditScope>(allowed[0] ?? 'this');
  useEffect(() => {
    if (open) value.value = allowed[0] ?? 'this';
  }, [open]);
  const options = (
    [
      {
        value: 'this' as const,
        label: m.design.scope.this,
        hint: t(a.scope.thisHint, { date: dateLabel }),
      },
      {
        value: 'this_and_following' as const,
        label: m.design.scope.following,
        hint: t(a.scope.followingHint, { date: dateLabel }),
      },
      { value: 'all' as const, label: m.design.scope.all, hint: a.scope.allHint },
    ] satisfies Array<{ value: EditScope; label: string; hint: string }>
  ).filter((o) => allowed.includes(o.value));
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={mode === 'edit' ? m.design.scope.editTitle : m.design.scope.deleteTitle}
      closeLabel={m.design.dialog.close}
      name="scope-dialog"
      actions={
        <>
          <Button name="scope-cancel" onPress={onClose}>
            {a.common.cancel}
          </Button>
          <Button
            name="scope-confirm"
            variant={mode === 'delete' ? 'danger' : 'primary'}
            busy={busy}
            onPress={() => onChoose(value.value)}
          >
            {mode === 'delete' ? a.common.delete : a.common.save}
          </Button>
        </>
      }
    >
      <RadioList
        name="scope"
        hideLabel
        label={mode === 'edit' ? m.design.scope.editTitle : m.design.scope.deleteTitle}
        options={options}
        value={value.value}
        onChange={(v) => (value.value = v)}
      />
    </Dialog>
  );
}
